import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchRetailSidebarSnapshot } from "@/lib/api/retail";
import { useTenantStore } from "@/stores/tenantStore";
import type { SidebarInfoSnapshot, SidebarInfoResult } from "@/features/restaurant/hooks/useRestaurantSidebarInfo";
import type { RetailSidebarSnapshot } from "@/features/retail/types/dashboard";

type RetailSidebarInfoSnapshot = SidebarInfoSnapshot & {
  registerCount: number;
  activeAssociates: number;
  totalAssociates: number;
};

const buildDefaultSnapshot = (fallbackName: string): RetailSidebarInfoSnapshot => ({
  name: fallbackName,
  status: "online",
  statusLabel: "Online",
  activeStaff: 0,
  totalStaff: 0,
  uptimePercent: 100,
  isDemo: true,
  lastUpdatedAt: null,
  registerCount: 0,
  activeAssociates: 0,
  totalAssociates: 0,
});

export const useRetailSidebarInfo = (): SidebarInfoResult & {
  info: RetailSidebarInfoSnapshot;
} => {
  const { activeRestaurantId, activeRestaurantName } = useTenantStore();

  const sidebarQuery = useQuery<RetailSidebarSnapshot | undefined>({
    queryKey: ["retail", "layout", "sidebar", activeRestaurantId],
    queryFn: ({ signal }) =>
      fetchRetailSidebarSnapshot({
        tenantId: activeRestaurantId,
        signal,
      }),
  });

  const info = useMemo<RetailSidebarInfoSnapshot>(() => {
    const snapshot = sidebarQuery.data;

    const fallbackName = activeRestaurantName ?? snapshot?.name ?? "Retail Store";
    const baseSnapshot = snapshot
      ? {
          name: snapshot.name,
          status: snapshot.status,
          statusLabel: snapshot.statusLabel,
          activeStaff: snapshot.activeAssociates,
          totalStaff: snapshot.totalAssociates,
          uptimePercent: snapshot.uptimePercent,
          isDemo: snapshot.isDemo,
          lastUpdatedAt: snapshot.lastSyncedAt,
          registerCount: snapshot.registerCount,
          activeAssociates: snapshot.activeAssociates,
          totalAssociates: snapshot.totalAssociates,
        }
      : buildDefaultSnapshot(fallbackName);

    return {
      name: fallbackName,
      status: baseSnapshot.status,
      statusLabel: baseSnapshot.statusLabel,
      activeStaff: baseSnapshot.activeStaff,
      totalStaff: baseSnapshot.totalStaff,
      uptimePercent: baseSnapshot.uptimePercent,
      isDemo: baseSnapshot.isDemo,
      lastUpdatedAt: baseSnapshot.lastUpdatedAt,
      registerCount: baseSnapshot.registerCount,
      activeAssociates: baseSnapshot.activeAssociates,
      totalAssociates: baseSnapshot.totalAssociates,
    };
  }, [activeRestaurantName, sidebarQuery.data]);

  return {
    info,
    isLoading: sidebarQuery.isLoading,
    isError: sidebarQuery.isError,
  };
};
